// apps/web/src/features/commercial-proposals/components/CommercialProposalPhotoControl.tsx
//
// @aiPurpose product-photo field for the CP draft editor (#1207): upload /
//   replace / clear with an inline preview and inline validation errors. The
//   bound key lives in form state (`form.photoObjectKey`) so save/dirty work
//   unchanged; this control only mutates it.
// @aiInvariant preview source is split: a freshly-selected (not-yet-saved)
//   file shows a LOCAL object URL — the presigned GET only resolves the
//   proposal's SAVED key, so it cannot preview an unsaved upload. The saved
//   key is previewed via the presigned download URL.
import { useEffect, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, IconButton } from '../../../ui/index.js';
import { requestCommercialProposalPhotoDownloadUrl } from '../../../api/commercial-proposals.js';
import {
  CP_PHOTO_ACCEPT_HEADER,
  useCommercialProposalPhotoUpload,
} from '../useCommercialProposalPhotoUpload.js';
// WHY (#1244): reuse the existing clipboard-image extractor that backs
// `/china-rates` rather than duplicating the MIME-whitelist logic.
import {
  extractFromClipboardApi,
  extractFromClipboardEvent,
  type ClipboardExtractError,
} from '../../requests/draft-media/index.js';

export function CommercialProposalPhotoControl({
  proposalId,
  savedPhotoObjectKey,
  value,
  onChange,
  onUploadingChange,
  disabled = false,
}: {
  /** Detail id, present only when the proposal is already saved; absent in
   *  the create host where no saved photo can exist yet. */
  proposalId: string | null;
  /** The photo key the proposal was LOADED with — used to decide whether
   *  `value` points at a server-saved photo (presigned preview) vs an
   *  unsaved local upload (object-URL preview). */
  savedPhotoObjectKey: string;
  /** Current form value (`form.photoObjectKey`); empty string == no photo. */
  value: string;
  onChange: (objectKey: string) => void;
  /** Reports whether an upload is in flight so the editor can block submit
   *  while a pending photo would otherwise be dropped. Reports `false` on
   *  unmount. */
  onUploadingChange?: (uploading: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('commercialProposals');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { uploading, error, errorFormat, upload, clearError } = useCommercialProposalPhotoUpload();
  // Clipboard extraction errors live in their own slot so an upload error and a
  // clipboard error never clobber each other.
  const [clipboardError, setClipboardError] = useState<ClipboardExtractError | null>(null);

  // Lift the in-flight flag to the editor's submit gate. Report `false` on
  // unmount so a control torn down mid-upload never leaves the parent stuck.
  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);
  useEffect(() => () => onUploadingChange?.(false), [onUploadingChange]);

  // WHY: a clear/replace/unmount during an in-flight upload must invalidate
  // that upload's late result, or its onChange would re-bind a key the user
  // already removed (or replaced). Each select captures this seq; on resolve
  // it applies only if still the latest. Bumped on clear and on new select.
  const requestSeq = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Local object-URL preview for a not-yet-saved upload. Revoked on
  // replace/clear/unmount so a selected blob never leaks.
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const localPreviewRef = useRef<string | null>(null);
  useEffect(() => {
    localPreviewRef.current = localPreview;
  }, [localPreview]);
  useEffect(
    () => () => {
      if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    },
    [],
  );

  const setLocal = (url: string | null): void => {
    if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    setLocalPreview(url);
  };

  // Presigned preview of the SAVED photo: only when the form still points at
  // the loaded key (no unsaved local replacement) and we have a proposal id.
  const showsSavedPhoto =
    !localPreview && value !== '' && value === savedPhotoObjectKey && proposalId !== null;
  const [savedSrc, setSavedSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!showsSavedPhoto || !proposalId) {
      setSavedSrc(null);
      return;
    }
    let active = true;
    requestCommercialProposalPhotoDownloadUrl(proposalId)
      .then((res) => {
        if (active) setSavedSrc(res.downloadUrl);
      })
      .catch(() => {
        if (active) setSavedSrc(null);
      });
    return () => {
      active = false;
    };
  }, [showsSavedPhoto, proposalId]);

  const onSelectFile = async (file: File): Promise<void> => {
    setClipboardError(null);
    const seq = ++requestSeq.current;
    const objectKey = await upload(file);
    // Drop a superseded (cleared/replaced) or post-unmount completion so it
    // cannot re-bind a key the user already removed or replaced.
    if (!objectKey || !mounted.current || seq !== requestSeq.current) return;
    setLocal(URL.createObjectURL(file));
    onChange(objectKey);
  };

  const onClear = (): void => {
    requestSeq.current += 1;
    setLocal(null);
    clearError();
    setClipboardError(null);
    onChange('');
  };

  // Read an image from the clipboard via the Async Clipboard API (button) or
  // the paste event (Ctrl/⌘-V), feeding the same select/upload path. Mirrors
  // `/china-rates` (D6); the upload hook compresses to JPEG before the PUT.
  const onClipboardButtonClick = async (): Promise<void> => {
    if (actionsDisabled) return;
    setClipboardError(null);
    const result = await extractFromClipboardApi();
    if (!result.ok) {
      setClipboardError(result.error);
      return;
    }
    void onSelectFile(result.file);
  };

  const onClipboardPaste = (e: ReactClipboardEvent<HTMLElement>): void => {
    if (actionsDisabled) return;
    const result = extractFromClipboardEvent(e.clipboardData);
    if (!result.ok) {
      setClipboardError(result.error);
      return;
    }
    e.preventDefault();
    void onSelectFile(result.file);
  };

  const previewSrc = localPreview ?? (showsSavedPhoto ? savedSrc : null);
  const hasPhoto = value !== '';
  const actionsDisabled = disabled || uploading;

  return (
    <div
      data-testid="commercial-proposals-photo-control"
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={CP_PHOTO_ACCEPT_HEADER}
        data-testid="commercial-proposals-photo-input"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void onSelectFile(file);
        }}
      />
      {previewSrc && (
        <img
          data-testid="commercial-proposals-photo-preview"
          src={previewSrc}
          alt={t('editor.photo.alt')}
          onError={() => setSavedSrc(null)}
          style={{
            width: 72,
            height: 54,
            objectFit: 'cover',
            borderRadius: 'var(--r-2)',
            border: '1px solid var(--border-1)',
          }}
        />
      )}
      <Button
        kind="ghost"
        size="sm"
        icon={hasPhoto ? 'refresh-cw' : 'upload-cloud'}
        disabled={actionsDisabled}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading
          ? t('editor.photo.uploading')
          : hasPhoto
            ? t('editor.photo.replace')
            : t('editor.photo.upload')}
      </Button>
      {/* WHY: the paste button stays wrapped in a `<span onPaste>` with no
          tabIndex so it does not hijack onPaste from sibling text inputs. */}
      <span style={{ display: 'inline-flex' }} onPaste={onClipboardPaste}>
        <Button
          kind="ghost"
          size="sm"
          icon="clipboard-paste"
          disabled={actionsDisabled}
          data-testid="commercial-proposals-photo-clipboard"
          onClick={() => void onClipboardButtonClick()}
        >
          {t('editor.photo.fromClipboard')}
        </Button>
      </span>
      {hasPhoto && (
        <IconButton
          icon="x"
          size={24}
          label={t('editor.photo.clear')}
          data-testid="commercial-proposals-photo-clear"
          onClick={onClear}
          disabled={actionsDisabled}
        />
      )}
      {error && (
        <span
          role="alert"
          data-testid="commercial-proposals-photo-error"
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {t(`editor.photo.errors.${error}`, { format: errorFormat })}
        </span>
      )}
      {clipboardError && (
        <span
          role="alert"
          data-testid="commercial-proposals-photo-clipboard-error"
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {t(`editor.photo.errors.${clipboardError}`)}
        </span>
      )}
    </div>
  );
}
